/** KYB (Know Your Business) editor — Phase 2H-kyb-e.
 *
 * Lets the org admin fill in the legal entity data we collect for
 * audit-grade evidence (legal_name, registration_number,
 * registration_jurisdiction, registered_address, vat_id) plus the
 * authorized-representative attestation. The PATCH triggers a
 * synchronous VIES validation when `vat_id` is set; the canonical
 * VIES name lands back in the read shape so the admin can
 * cross-check it against the typed legal_name.
 *
 * What the customer admin sees:
 *   - Editable fields
 *   - VIES canonical name + timestamp (informational; mismatch is a
 *     soft flag — ops reviews)
 *   - Sanctions screened state (timestamp + match boolean only —
 *     match DETAILS are deliberately hidden from the admin)
 *   - KYC status badge (unverified / pending / verified / rejected).
 *     The endpoint does NOT let admins self-promote; ops flips the
 *     status via Django admin after reviewing the submission.
 *
 * QES (Level 3) signature issuance + signed audit-report PDFs gate on
 * `kyc_status === "verified"` (Phase 2H-kyb-d). SES / AES + the
 * unsigned audit report stay open regardless.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "@/lib/format";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type { KycStatus, OrgKyb, OrgKybInput } from "@/lib/types";

type FormState = {
  legal_name: string;
  registration_number: string;
  registration_jurisdiction: string;
  registered_address: string;
  vat_id: string;
  attested: boolean;
};

const EMPTY: FormState = {
  legal_name: "",
  registration_number: "",
  registration_jurisdiction: "",
  registered_address: "",
  vat_id: "",
  attested: false,
};

// Phase 2H-kyb-c v1.5 — jurisdictions where the backend dispatcher has
// a registry validator (live or format-only). Free-text fallback covers
// anything else (rare; keep the select narrow to avoid choice overload).
const SUPPORTED_JURISDICTIONS: { code: string; label: string }[] = [
  { code: "FR", label: "France (Sirene — live)" },
  { code: "GB", label: "United Kingdom (Companies House — live)" },
  { code: "NL", label: "Netherlands (KvK — live)" },
  { code: "DE", label: "Germany (Handelsregister — format only)" },
  { code: "ES", label: "Spain (CIF — checksum only)" },
  { code: "IT", label: "Italy (Partita IVA — checksum only)" },
  { code: "BE", label: "Belgium (KBO/BCE — checksum only)" },
];

const REGISTRATION_NUMBER_HINTS: Record<string, string> = {
  FR: "14-digit SIRET (no spaces).",
  GB: "8-character Companies House number — `12345678` or `SC123456`.",
  NL: "8-digit KvK number (7-digit legacy auto-pads).",
  DE: "Handelsregister number — `HRB 12345`, `HRA 6789`, or court-prefixed.",
  ES: "CIF — leading letter + 7 digits + check char (e.g. `A12345674`).",
  IT: "11-digit Partita IVA (optional `IT` prefix accepted).",
  BE: "10-digit KBO/BCE (`0123.456.749` formatting OK).",
};

const STATUS_TONE: Record<KycStatus, string> = {
  unverified: "border-nb-border bg-nb-surface/40 text-nb-text",
  pending: "border-amber-500 bg-amber-500/10 text-amber-200",
  verified: "border-emerald-500 bg-emerald-500/10 text-emerald-300",
  rejected: "border-cvp-danger bg-cvp-danger/15 text-cvp-danger",
};

const STATUS_KEY: Record<KycStatus, string> = {
  unverified: "admin.kyb.status.unverified",
  pending: "admin.kyb.status.pending",
  verified: "admin.kyb.status.verified",
  rejected: "admin.kyb.status.rejected",
};

export function KybConfigCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [seeded, setSeeded] = useState(false);

  const kyb = useQuery({
    queryKey: ["org-kyb"],
    queryFn: async () => {
      const res = await api.get<OrgKyb>(ApiPaths.orgKyb);
      return res.data;
    },
  });

  // Seed once — don't clobber user edits on background refetch.
  useEffect(() => {
    if (seeded || !kyb.data) return;
    setForm({
      legal_name: kyb.data.legal_name || "",
      registration_number: kyb.data.registration_number || "",
      registration_jurisdiction: kyb.data.registration_jurisdiction || "",
      registered_address: kyb.data.registered_address || "",
      vat_id: kyb.data.vat_id || "",
      attested: false, // attestation is a per-submission act, not stored on the form
    });
    setSeeded(true);
  }, [kyb.data, seeded]);

  const save = useMutation({
    mutationFn: async (input: OrgKybInput) => {
      const res = await api.patch<OrgKyb>(ApiPaths.orgKyb, input);
      return res.data;
    },
    onSuccess: (next) => {
      qc.setQueryData(["org-kyb"], next);
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    save.mutate({
      legal_name: form.legal_name.trim(),
      registration_number: form.registration_number.trim(),
      registration_jurisdiction: form.registration_jurisdiction.trim(),
      registered_address: form.registered_address.trim(),
      vat_id: form.vat_id.trim(),
      attested: form.attested,
    });
  };

  if (kyb.isLoading && !kyb.isFetched) {
    return (
      <section className="card text-sm text-nb-text-2">
        {t("admin.kyb.loading")}
      </section>
    );
  }
  if (!kyb.data) {
    return null;
  }

  const status = kyb.data.kyc_status;
  const vatValidated = !!kyb.data.vat_validated_at;
  const vatCanonical = kyb.data.vat_validated_name;
  const registryValidated = !!kyb.data.registry_validated_at;
  const registryCanonical = kyb.data.registry_validated_name;
  const registryIsActive = kyb.data.registry_is_active;
  // Phase 2H-kyb-c (DE) — DE has no free live lookup at v1, so the
  // server emits `lookup_supported=false` after format validation.
  // UI shows "format validated, please verify manually" instead of
  // the misleading "did not recognise" we use for FR/GB not-found.
  const registryLookupSupported = kyb.data.registry_lookup_supported;
  const sanctionsScreenedAt = kyb.data.sanctions_screened_at;
  const sanctionsFlagged = kyb.data.sanctions_match_found;

  return (
    <section className="card space-y-4" data-testid="kyb-card">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.kyb.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">{t("admin.kyb.cardHint")}</p>
      </header>

      {/* Status badge */}
      <div className="flex items-center gap-3">
        <span className="text-xs font-medium uppercase tracking-wide text-nb-text-2">
          {t("admin.kyb.statusLabel")}
        </span>
        <span
          data-testid="kyb-status-badge"
          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${STATUS_TONE[status]}`}
        >
          {t(STATUS_KEY[status])}
        </span>
      </div>

      <form className="space-y-3" onSubmit={onSubmit}>
        <div>
          <label className="label" htmlFor="legal_name">
            {t("admin.kyb.legalName")}
          </label>
          <input
            id="legal_name"
            className="input"
            value={form.legal_name}
            placeholder={t("admin.kyb.legalNamePlaceholder")}
            onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
          />
          <p className="mt-1 text-xs text-nb-text-3">
            {t("admin.kyb.legalNameHint")}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="registration_number">
              {t("admin.kyb.registrationNumber")}
            </label>
            <input
              id="registration_number"
              className="input"
              value={form.registration_number}
              placeholder={t("admin.kyb.registrationNumberPlaceholder")}
              onChange={(e) =>
                setForm({ ...form, registration_number: e.target.value })
              }
            />
            {/* Phase 2H-kyb-c v1.5 — per-jurisdiction format hint so
                admins know the canonical shape before submitting. */}
            {REGISTRATION_NUMBER_HINTS[form.registration_jurisdiction] && (
              <p
                className="mt-1 text-xs text-nb-text-3"
                data-testid="registration-number-hint"
              >
                {REGISTRATION_NUMBER_HINTS[form.registration_jurisdiction]}
              </p>
            )}
          </div>
          <div>
            <label className="label" htmlFor="registration_jurisdiction">
              {t("admin.kyb.jurisdiction")}
            </label>
            {/* Country picker — narrow to the jurisdictions our
                registry dispatcher knows about so admins don't pick
                something we silently fall-through. Free-text fallback
                covers anything else (stays a 2-char ISO code). */}
            <select
              id="registration_jurisdiction"
              className="input"
              data-testid="registration-jurisdiction-select"
              value={form.registration_jurisdiction}
              onChange={(e) =>
                setForm({
                  ...form,
                  registration_jurisdiction: e.target.value,
                })
              }
            >
              <option value="">{t("admin.kyb.jurisdictionPlaceholder")}</option>
              {SUPPORTED_JURISDICTIONS.map((j) => (
                <option key={j.code} value={j.code}>
                  {j.code} — {j.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {registryValidated && (
          <div
            className={`rounded-md border px-3 py-2 text-xs ${
              !registryLookupSupported
                ? "border-nb-border bg-slate-900/40 text-nb-text"
                : registryCanonical && registryIsActive
                  ? "border-nb-border bg-slate-900/40 text-nb-text"
                  : "border-cvp-warn bg-cvp-warn/10 text-amber-200"
            }`}
            data-testid="registry-validated-row"
          >
            <div className="text-nb-text-2">
              {registryLookupSupported
                ? t("admin.kyb.registryValidatedAt", {
                    when: kyb.data.registry_validated_at
                      ? formatDateTime(kyb.data.registry_validated_at)
                      : "",
                  })
                : t("admin.kyb.registryFormatValidatedAt", {
                    when: kyb.data.registry_validated_at
                      ? formatDateTime(kyb.data.registry_validated_at)
                      : "",
                  })}
            </div>
            {!registryLookupSupported ? (
              // DE / other jurisdictions without a free live API —
              // we validated the format; ops verifies manually.
              <div className="mt-0.5 text-nb-text">
                {t("admin.kyb.registryDeNotice")}
              </div>
            ) : !registryCanonical ? (
              <div className="mt-0.5 text-cvp-warn">
                {t("admin.kyb.registryNotRecognised")}
              </div>
            ) : !registryIsActive ? (
              <div className="mt-0.5 text-cvp-warn">
                {t("admin.kyb.registryInactive", { name: registryCanonical })}
              </div>
            ) : (
              <div className="mt-0.5 text-nb-text">
                {t("admin.kyb.registryCanonicalName", {
                  name: registryCanonical,
                })}
              </div>
            )}
          </div>
        )}

        <div>
          <label className="label" htmlFor="registered_address">
            {t("admin.kyb.registeredAddress")}
          </label>
          <textarea
            id="registered_address"
            className="input min-h-[80px]"
            value={form.registered_address}
            placeholder={t("admin.kyb.registeredAddressPlaceholder")}
            onChange={(e) =>
              setForm({ ...form, registered_address: e.target.value })
            }
          />
        </div>
        <div>
          <label className="label" htmlFor="vat_id">
            {t("admin.kyb.vatId")}
          </label>
          <input
            id="vat_id"
            className="input"
            value={form.vat_id}
            placeholder={t("admin.kyb.vatIdPlaceholder")}
            onChange={(e) => setForm({ ...form, vat_id: e.target.value })}
          />
          {vatValidated && (
            <div
              className="mt-2 rounded-md border border-nb-border bg-slate-900/40 px-3 py-2 text-xs"
              data-testid="vat-validated-row"
            >
              <div className="text-nb-text-2">
                {t("admin.kyb.viesValidatedAt", {
                  when: kyb.data.vat_validated_at
                    ? formatDateTime(kyb.data.vat_validated_at)
                    : "",
                })}
              </div>
              {vatCanonical ? (
                <div className="mt-0.5 text-nb-text">
                  {t("admin.kyb.viesCanonicalName", { name: vatCanonical })}
                </div>
              ) : (
                <div className="mt-0.5 text-cvp-warn">
                  {t("admin.kyb.viesNotRecognised")}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sanctions screened state — read-only */}
        {sanctionsScreenedAt && (
          <div
            className={`rounded-md border px-3 py-2 text-xs ${
              sanctionsFlagged
                ? "border-cvp-danger bg-cvp-danger/10 text-cvp-danger"
                : "border-nb-border bg-slate-900/40 text-nb-text"
            }`}
            data-testid="sanctions-state"
          >
            <div className="font-medium uppercase tracking-wide">
              {t("admin.kyb.sanctionsTitle")}
            </div>
            <div className="mt-0.5">
              {sanctionsFlagged
                ? t("admin.kyb.sanctionsFlagged")
                : t("admin.kyb.sanctionsClean", {
                    when: formatDateTime(sanctionsScreenedAt),
                  })}
            </div>
          </div>
        )}

        {/* Attestation */}
        <div className="rounded-md border border-nb-border bg-slate-900/40 px-3 py-3">
          <label className="flex items-start gap-2 text-sm text-nb-text">
            <input
              type="checkbox"
              className="mt-1"
              checked={form.attested}
              onChange={(e) =>
                setForm({ ...form, attested: e.target.checked })
              }
              data-testid="kyb-attest-checkbox"
            />
            <span>
              {t("admin.kyb.attestationPrefix")}{" "}
              <span className="font-medium">
                {form.legal_name || t("admin.kyb.attestationDefaultName")}
              </span>{" "}
              {t("admin.kyb.attestationSuffix")}
            </span>
          </label>
          {kyb.data.kyc_attested_at && (
            <p className="mt-2 text-[11px] text-nb-text-3">
              {t("admin.kyb.lastAttested", {
                when: formatDateTime(kyb.data.kyc_attested_at),
              })}
              {kyb.data.kyc_attested_by_ip
                ? t("admin.kyb.lastAttestedFromIp", {
                    ip: kyb.data.kyc_attested_by_ip,
                  })
                : ""}
              .
            </p>
          )}
        </div>

        {save.isError && (
          <p className="text-xs text-cvp-danger" role="alert">
            {(() => {
              const err = save.error as unknown as {
                response?: { data?: unknown };
              };
              return err?.response?.data
                ? JSON.stringify(err.response.data)
                : t("admin.kyb.saveErrorGeneric");
            })()}
          </p>
        )}
        {save.isSuccess && (
          <p className="text-xs text-emerald-400" role="status">
            {t("admin.kyb.saveSuccess")}
          </p>
        )}

        <button
          type="submit"
          className="btn-primary text-sm"
          disabled={save.isPending || !form.legal_name.trim()}
        >
          {save.isPending
            ? t("admin.kyb.submitting")
            : t("admin.kyb.submitButton")}
        </button>
      </form>
    </section>
  );
}
